import type { ObAppointmentRow, ObDashboardOverviewResponse, ObMetricCardData } from './dashboard';
import { adaptDashboardMetrics } from './dashboard';
import { buildReportKpiMetrics } from './report';

export const OB_MOCK_OVERVIEW: ObDashboardOverviewResponse = {
  current_period: {
    total_appointments: 128,
    average_visit: 142,
    money_collected: 18450,
    booked_appointments: 96,
    upsell_count: 18,
    conversion_rate: 64.5,
    insurance_checked: 72,
    ai_verified_appointments: 54,
  },
  previous_period: {
    total_appointments: 110,
    average_visit: 128,
    money_collected: 15200,
    booked_appointments: 84,
    upsell_count: 12,
    conversion_rate: 58.2,
    insurance_checked: 61,
    ai_verified_appointments: 41,
  },
  changes: {
    total_appointments: { value: 18, percentage: 16.4 },
    average_visit: { value: 14, percentage: 10.9 },
    money_collected: { value: 3250, percentage: 21.4 },
    booked_appointments: { value: 12, percentage: 14.3 },
    upsell_count: { value: 6, percentage: 50 },
    conversion_rate: { value: 6.3, percentage: 10.8 },
    insurance_checked: { value: 11, percentage: 18 },
  },
  period_info: {
    current_start: '2026-07-01',
    current_end: '2026-07-31',
    previous_start: '2026-06-01',
    previous_end: '2026-06-30',
  },
};

export const OB_MOCK_DASHBOARD_METRICS: ObMetricCardData[] =
  adaptDashboardMetrics(OB_MOCK_OVERVIEW);

export const OB_MOCK_REPORT_METRICS: ObMetricCardData[] = buildReportKpiMetrics({
  totalAppointments: OB_MOCK_OVERVIEW.current_period.total_appointments,
  moneyCollected: OB_MOCK_OVERVIEW.current_period.money_collected,
  totalAIVerified: OB_MOCK_OVERVIEW.current_period.ai_verified_appointments ?? 0,
  previous: {
    totalAppointments: OB_MOCK_OVERVIEW.previous_period.total_appointments,
    moneyCollected: OB_MOCK_OVERVIEW.previous_period.money_collected,
    totalAIVerified: OB_MOCK_OVERVIEW.previous_period.ai_verified_appointments ?? 0,
  },
});

export const OB_MOCK_APPOINTMENTS: ObAppointmentRow[] = [
  {
    id: 9001,
    patient_name: 'Olivia Rhye',
    patient_phone: '+1 (415) 555-0142',
    patient_email: 'olivia@example.com',
    appointment_date: '2026-07-28',
    appointment_time: '09:30:00',
    service_name: 'Emergency',
    provider_name: 'Dr. John Smith',
    verification_status: 'verified',
    created_at: '2026-07-27T14:20:00Z',
    is_booked: true,
    is_payment_done: true,
    is_sms_verified: true,
    is_call_verified: true,
    pre_payment_amount: '75',
    call_transcription:
      'Patient confirmed emergency visit. Insurance verified. Prefers morning slot.',
    call_audio: null,
    existing_patient: null,
  },
  {
    id: 9002,
    patient_name: 'Phoenix Baker',
    patient_phone: '+1 (415) 555-0198',
    patient_email: 'phoenix@example.com',
    appointment_date: '2026-07-29',
    appointment_time: '11:00:00',
    service_name: 'Cleaning',
    provider_name: 'Dr. Sarah Lee',
    verification_status: 'pending',
    created_at: '2026-07-28T10:05:00Z',
    is_booked: false,
    is_payment_done: false,
    is_sms_verified: true,
    is_call_verified: false,
    pre_payment_amount: '40',
    call_transcription: '',
    call_audio: null,
    existing_patient: 12,
  },
  {
    id: 9003,
    patient_name: 'Lana Steiner',
    patient_phone: '+1 (628) 555-0110',
    patient_email: 'lana@example.com',
    appointment_date: '2026-07-30',
    appointment_time: '15:45:00',
    service_name: 'Whitening',
    provider_name: 'Dr. John Smith',
    verification_status: 'booked',
    created_at: '2026-07-29T08:40:00Z',
    is_booked: true,
    is_payment_done: false,
    is_sms_verified: false,
    is_call_verified: false,
    pre_payment_amount: '120',
    call_transcription: 'Patient asked about whitening duration and aftercare.',
    call_audio: null,
    existing_patient: 44,
  },
  {
    id: 9004,
    patient_name: 'Drew Cano',
    patient_phone: '+1 (510) 555-0166',
    patient_email: 'drew@example.com',
    appointment_date: '2026-07-31',
    appointment_time: '13:15:00',
    service_name: 'Consultation',
    provider_name: 'Dr. Maya Patel',
    verification_status: 'payment',
    created_at: '2026-07-30T16:10:00Z',
    is_booked: true,
    is_payment_done: false,
    is_sms_verified: true,
    is_call_verified: true,
    pre_payment_amount: '50',
    call_transcription: '',
    call_audio: null,
    existing_patient: null,
  },
];

export const OB_MOCK_PROVIDERS = [
  {
    id: 101,
    full_name: 'Dr. John Smith',
    specialty: 'General Dentistry',
    has_online_booking: true,
    services: [
      { id: 1, name: 'Cleaning' },
      { id: 2, name: 'Emergency' },
    ],
    default_operatory_label: 'Operatory #1',
  },
  {
    id: 102,
    full_name: 'Dr. Sarah Lee',
    specialty: 'Hygiene',
    has_online_booking: true,
    services: [{ id: 1, name: 'Cleaning' }],
    default_operatory_label: 'Operatory #2',
  },
  {
    id: 103,
    full_name: 'Dr. Maya Patel',
    specialty: 'Cosmetic',
    has_online_booking: false,
    services: [
      { id: 3, name: 'Whitening' },
      { id: 4, name: 'Consultation' },
    ],
    default_operatory_label: 'Operatory #3',
  },
];

export const OB_MOCK_SERVICES = [
  {
    id: 1,
    name: 'Cleaning',
    description: 'Routine dental cleaning and exam',
    pre_payment_required: true,
    pre_payment_amount: '40',
    double_booking_allowed: false,
    default_duration_minutes: 45,
    is_active: true,
    providers_count: 2,
    total_booking: 38,
  },
  {
    id: 2,
    name: 'Emergency',
    description: 'Same-day pain or urgent care',
    pre_payment_required: true,
    pre_payment_amount: '75',
    double_booking_allowed: true,
    default_duration_minutes: 30,
    is_active: true,
    providers_count: 1,
    total_booking: 12,
  },
  {
    id: 3,
    name: 'Whitening',
    description: 'In-office whitening treatment',
    pre_payment_required: true,
    pre_payment_amount: '120',
    double_booking_allowed: false,
    default_duration_minutes: 60,
    is_active: true,
    providers_count: 1,
    total_booking: 9,
  },
  {
    id: 4,
    name: 'Consultation',
    description: 'New patient consultation',
    pre_payment_required: false,
    pre_payment_amount: '0',
    double_booking_allowed: false,
    default_duration_minutes: 20,
    is_active: true,
    providers_count: 2,
    total_booking: 21,
  },
];

export const OB_MOCK_PROVIDER_OPTIONS = [
  { value: 'all', label: 'All Providers' },
  ...OB_MOCK_PROVIDERS.map((p) => ({ value: String(p.id), label: p.full_name })),
];

export const OB_MOCK_SERVICE_OPTIONS = [
  { value: 'all', label: 'All Services' },
  ...OB_MOCK_SERVICES.map((s) => ({ value: String(s.id), label: s.name })),
];

const themePlaceholder = (seed: string, label: string) =>
  `https://placehold.co/640x360/E8F5F1/1D1D1F/png?text=${encodeURIComponent(label)}&font=source-sans-pro`;

export const OB_MOCK_THEMES = [
  {
    id: 11,
    name: 'Sage Calm',
    preview_image: themePlaceholder('11', 'Sage+Calm'),
    images: [
      {
        id: 111,
        image: themePlaceholder('11a', 'Sage+1'),
        image_preview: themePlaceholder('11a', 'Sage+1'),
        name: 'Sage 1',
      },
      {
        id: 112,
        image: themePlaceholder('11b', 'Sage+2'),
        image_preview: themePlaceholder('11b', 'Sage+2'),
        name: 'Sage 2',
      },
    ],
  },
  {
    id: 21,
    name: 'Coastal Light',
    preview_image: themePlaceholder('21', 'Coastal'),
    images: [
      {
        id: 211,
        image: themePlaceholder('21a', 'Coastal+1'),
        image_preview: themePlaceholder('21a', 'Coastal+1'),
        name: 'Coastal 1',
      },
      {
        id: 212,
        image: themePlaceholder('21b', 'Coastal+2'),
        image_preview: themePlaceholder('21b', 'Coastal+2'),
        name: 'Coastal 2',
      },
    ],
  },
  {
    id: 31,
    name: 'Warm Studio',
    preview_image: themePlaceholder('31', 'Warm+Studio'),
    images: [
      {
        id: 311,
        image: themePlaceholder('31a', 'Warm+1'),
        image_preview: themePlaceholder('31a', 'Warm+1'),
        name: 'Warm 1',
      },
      {
        id: 312,
        image: themePlaceholder('31b', 'Warm+2'),
        image_preview: themePlaceholder('31b', 'Warm+2'),
        name: 'Warm 2',
      },
    ],
  },
];

export const OB_MOCK_AI_FORM = {
  name: 'Elva Booking Agent',
  retell_voice_id: 'mock-voice-ava',
  greeting_message:
    'Hi, thanks for calling. I can help you book an appointment online. What day works best for you?',
  grounding_rules:
    'Only discuss scheduling, services, and clinic hours. Escalate medical advice to staff.',
  faq: 'Q: Do you accept new patients?\nA: Yes, we welcome new patients.\n\nQ: Is there a prepayment?\nA: Some services require a small deposit.',
};

export const OB_MOCK_RETELL_VOICES = [
  { id: 'mock-voice-ava', name: 'Ava', accent: 'American', gender: 'Female' },
  { id: 'mock-voice-noah', name: 'Noah', accent: 'American', gender: 'Male' },
  { id: 'mock-voice-sofia', name: 'Sofia', accent: 'British', gender: 'Female' },
];

export const OB_MOCK_TESTIMONIALS_FORM = {
  title: 'Loved by our patients',
  subtitle: 'Real stories from people who booked online',
  first_five_star_name: 'Jordan M.',
  first_five_star_subtitle: 'Booked cleaning in under 2 minutes',
  first_five_star_tm:
    'The online booking was so easy. I picked a time, verified insurance, and was done.',
  second_five_star_name: 'Casey R.',
  second_five_star_subtitle: 'Emergency slot the same day',
  second_five_star_tm:
    'I needed an urgent visit and found an open slot immediately. Super smooth experience.',
};

export function obFallbackList<T>(live: T[] | null | undefined, mock: T[]): T[] {
  return live && live.length > 0 ? live : mock;
}
